import React, { useEffect, useCallback } from 'react';
import { useRecoilValue, SetterOrUpdater } from 'recoil';
import Cookies from 'universal-cookie';
import { groupBy, maxBy, forEach } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { notificationsState } from '../recoil-context';

export enum NotiType {
  SUCCESS = 'lv-success',
  WARNING = 'lv-emergency',
  INFO = 'default',
}

interface NotificationProps {
  type: NotiType;
  message: string;
  uniqueId?: string;
}

interface NotificationItem extends NotificationProps {
  timestamp: number;
  component: JSX.Element;
}

interface SatelliteTracker {
  track?: (event: string) => void;
}

declare global {
  var _satellite: SatelliteTracker | undefined;
  
  namespace JSX {
    interface IntrinsicElements {
      'cbw-notification': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          type: string;
          nId: string;
        },
        HTMLElement
      >;
      'cbw-notifications': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

const cookies = new Cookies();

/**
 * Creates a new notification with a unique ID and timestamp
 */
const createNotification = (props: NotificationProps): NotificationItem => {
  const { type, message, uniqueId = uuidv4() } = props;

  return {
    type,
    message,
    uniqueId,
    timestamp: Date.now(),
    component: (
      <cbw-notification key={uniqueId} type={type} nId={uniqueId}>
        {message}
      </cbw-notification>
    ),
  };
};

/**
 * Removes duplicate notifications keeping only the most recent one
 */
const removeDuplicates = (notifications: NotificationItem[]): NotificationItem[] => {
  const finalNotifications: NotificationItem[] = [];
  const groupedByUniqueId = groupBy(notifications, 'uniqueId');
  forEach(groupedByUniqueId, (notificationsForId) => {
    const mostRecent = maxBy(notificationsForId, 'timestamp');
    if (mostRecent) {
      finalNotifications.push(mostRecent);
    }
  });

  return finalNotifications.sort((a, b) => b.timestamp - a.timestamp);
};

/**
 * Adds a new notification to the state
 */
export const addNotification = (
  props: NotificationProps,
  setNotifications: SetterOrUpdater<NotificationItem[]>
): void => {
  setNotifications((prevState) => [...prevState, createNotification(props)]);
};

interface GlobalNotificationProps {
  className?: string;
}

/**
 * Global notification component that displays notifications using the CBW notification widget
 */
export const GlobalNotification: React.FC<GlobalNotificationProps> = ({
  className
}) => {
  const notifications = useRecoilValue(notificationsState);

  const getNotifications = useCallback(() => {
    return removeDuplicates([...notifications]).map(
      (notification) => notification.component
    );
  }, [notifications]);

  useEffect(() => {
    if (globalThis._satellite?.track) {
      try {
        globalThis._satellite.track('cbTrack.viewInDom');
      } catch (error) {
        console.warn('Failed to track notification view:', error);
      }
    }

    // Clean up old notifications cookie
    cookies.remove('lv-notificationWidget-storage-cookie');
  }, []);

  if (!notifications?.length) {
    return null;
  }

  return (
    <cbw-notifications className={className}>
      {getNotifications()}
    </cbw-notifications>
  );
};

export default GlobalNotification;
